import { ApplicationVersion } from '@kw/device-service/dist/application/entities/applicationVersion.entity';
import { PolicyDetails } from '@kw/service-definitions-eval';
import { createQueryString } from '../../../utils/createQueryString';
import { useFetchService } from '../../../utils/fetchService';
import { useStickyResult } from '../../../utils/useStickyResult';

const sleep = (ms: number) =>
  new Promise<void>(resolve => {
    setTimeout(resolve, ms);
  });

export const useAppVersions = (endpoint, appParams, accessToken, shouldFetch = true) => {
  const queryParams = createQueryString(appParams);

  const path = `${endpoint}?${queryParams}`;

  const {
    data: appData,
    error: appsDataError,
    mutate,
  } = useFetchService<{
    // `displayVersion` is the eval-service search view's clean, display-only version (manifest version
    // for uploaded binaries; equals the normal version for store apps). Distinct from the identity
    // `applicationVersion`. The Applications table reads it via getDisplayedAppVersion.
    apps: (ApplicationVersion & { displayVersion?: string; merlinThreatScore?: number; merlinDesignation?: string })[];
    policies: PolicyDetails[];
    totalApps: number;
    totalDevices: number;
    totalManagedApps: number;
    totalDiscoveredApps: number;
  }>(path, accessToken, shouldFetch);
  const { apps, policies, totalApps = 0, totalDevices, totalManagedApps, totalDiscoveredApps } = useStickyResult(appData);
  const isLoading = !appData && !appsDataError;

  const mutateWithDelay = async (ms = 1500) => {
    await sleep(ms);
    return mutate(); // revalidate now
  };

  return {
    apps,
    policies,
    totalApps,
    totalDevices,
    totalManagedApps,
    totalDiscoveredApps,
    appsDataError,
    isLoading,
    mutate,
    mutateWithDelay,
  };
};

export const useHighRiskCountries = (accessToken: string) => {
  const { data, error: highRiskCountriesError } = useFetchService<{ highRiskCountries: string[]; total: number }>(
    '/list/high-risk-countries',
    accessToken,
  );
  const { highRiskCountries, total: highRiskCountriesTotal } = useStickyResult(data);

  return { highRiskCountries, highRiskCountriesTotal, highRiskCountriesError };
};

export const useCategories = (accessToken: string) => {
  const { data, error: categoriesError } = useFetchService<{ categories: string[] }>('/eval/applications/category-list', accessToken);
  const { categories } = useStickyResult(data);

  return { categories, categoriesError };
};
